import React, { useState } from "react";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { getFirestore, collection, addDoc, Timestamp } from "firebase/firestore";
import { app } from "../../../firebase";

const FileUploadModal = ({ folderId, onClose, onUploaded }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({}); // { filename: { percent, loaded, total } }
  const db = getFirestore(app);
  const storage = getStorage(app);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleUpload = async () => {
    setUploading(true);
    setProgress({});
    let completed = 0;
    for (const file of files) {
      const storageRef = ref(storage, `fileStorage/${folderId}/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setProgress(prev => ({
              ...prev,
              [file.name]: {
                percent,
                loaded: snapshot.bytesTransferred,
                total: snapshot.totalBytes
              }
            }));
          },
          reject,
          async () => {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            const now = Timestamp.now();
            const accurateSize = uploadTask.snapshot.totalBytes || file.size || 0;
            await addDoc(collection(db, "fileStorages"), {
              name: file.name,
              folderId,
              downloadUrl,
              type: file.type,
              size: accurateSize,
              createdAt: now,
              updatedAt: now,
              modifiedAt: now,
            });
            completed++;
            resolve();
          }
        );
      });
    }
    setUploading(false);
    setTimeout(() => {
      if (typeof onUploaded === 'function') onUploaded();
      if (typeof onClose === 'function') onClose();
    }, 500);
  };

  // Public upload link (icon only UI)
    const publicLink = `${window.location.origin}/origin/public-folder-upload`;
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
      navigator.clipboard.writeText(publicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
  

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-lg relative animate-fadeIn">
        <button className="absolute top-3 right-3 text-gray-400 hover:text-blue-600 text-xl" onClick={onClose} disabled={uploading}>&times;</button>
       
        <h3 className="text-2xl font-extrabold text-blue-700 mb-6 text-center">Upload Files</h3>
        <input type="file" multiple onChange={handleFileChange} className="mb-6 w-full border border-gray-200 rounded-lg px-3 py-2" disabled={uploading} />
        {files.length > 0 && (
          <ul className="mb-4 max-h-40 overflow-y-auto divide-y divide-gray-100">
            {files.map(file => (
              <li key={file.name} className="flex items-center py-2 text-sm">
                <span className="truncate flex-1 font-medium text-gray-700">{file.name}</span>
                <span className="ml-2 text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</span>
                {uploading && progress[file.name] && (
                  <span className="ml-4 flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                    <span className="font-semibold text-blue-700">{progress[file.name].percent}%</span>
                    <span className="text-xs text-gray-400">({progress[file.name].loaded} / {progress[file.name].total} bytes)</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {uploading && (
          <div className="flex flex-col items-center mb-4">
            <svg className="animate-spin h-8 w-8 text-blue-600 mb-2" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
            <div className="text-blue-700 font-semibold">Uploading files...</div>
          </div>
        )}
        <div className="flex justify-end space-x-2 mt-6">
          <button className="px-4 py-2 bg-gray-200 rounded-lg font-semibold" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="px-4 py-2 bg-[#0071bd] text-white rounded-lg font-semibold shadow hover:bg-[#005fa3] transition" onClick={handleUpload} disabled={uploading || files.length === 0}>
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
